import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { StepOne } from './Step1';
import { StepTwo } from './Step2';
import { StepThree } from './Step3';
import { StepFour } from './Step4';
import { StepFive } from './Step5';
import { StepSix } from './Step6';
import { StepSeven } from './Step7';
import { StepEight } from './Step8';
import { StepNine } from './Step9';
import { StepTen } from './Step10';
import { StepEleven } from './Step11';
import getParagraphs from '../../api/getParagraphs';
import getPrompts from '../../api/getPrompt';
import getStatements from '../../api/getStatements';
import grappleAi from '../../api/grappleAi';
import getAdviceNotes from '../../api/getAdviceNote';
import generateAdviceNote from '../../api/generateAdviceNote';
import checkAvailability from '../../api/checkAvailability';
import { legacyParseFirstJsonInResponse } from '../../utils/common/legacyCompatibility';
import {
  saveGrappleReferral,
  thankYouPageFor,
} from '../../utils/grappleReferral';
import getTrackingParams from '../../utils/getTrackingParams';
import getYearsEmployedLabelByValue from '../../constants/formFields/yearsEmployed';
import getStillEmployedLabelByValue from '../../constants/formFields/stillEmployed';

const validationSchema = [
  Yup.object({
    'still-employed': Yup.string().required('Required'),
    'years-employed': Yup.string().required('Required'),
  }),
  Yup.object({
    salary: Yup.string().required('Required'),
  }),
  Yup.object({
    'settlement-agreement': Yup.string(),
    'what-is-your-objective': Yup.string(),
  }),
  Yup.object({
    stages: Yup.string(),
  }),
  Yup.object({
    employment_issues: Yup.array(Yup.string())
      .min(1, 'At least one employment issue must be selected')
      .required('Employment issues are required'),
  }),
  Yup.object({
    other_issues: Yup.array(Yup.string())
      .min(1, 'At least one discrimination issue must be selected')
      .required('Discrimination issues are required'),
  }),
  Yup.object({
    case_summary: Yup.string().required('Required'),
  }),
  Yup.object({
    questions: Yup.string(),
  }),
  Yup.object({
    statementList: Yup.array(Yup.string()),
  }),
  Yup.object({
    name: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email format').required('Required'),
  }),
];

export const ContactUsSteps = () => {
  const router = useRouter();
  const isUserLoggedIn = router.query.loggedIn === 'true';
  const [step, setStep] = useState(0);
  const [statementslist, setStatementsList] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [paraPrompt, setParaPrompt] = useState('');
  const steps = [
    <StepTwo key="2" />,
    <StepThree key="3" errors={Yup.ValidationError} />,
    <StepFour key="4" />,
    <StepFive key="5" />,
    <StepSix key="6" />,
    <StepSeven key="7" />,
    <StepEight key="8" />,
    <StepNine key="9" />,
    <StepTen key="10" statementslist={statementslist} />,
    <StepOne key="1" />,
    <StepEleven key="11" />,
  ];

  const employmentIssues = [
    { value: 'B', label: 'Bullying' },
    { value: 'RR', label: 'Redundancy' },
    { value: 'W', label: 'Whistleblowing' },
    { value: 'S', label: 'Sickness' },
    { value: 'Sn', label: 'Suspension' },
    { value: 'Dy', label: 'Disciplinary' },
    { value: 'H', label: 'Unsafe workplace' },
    { value: 'P', label: 'Performance' },
    { value: 'TWE', label: 'Toxic work environment' },
    { value: 'EW', label: 'Excessive workload' },
    { value: 'M', label: 'Money owed' },
    { value: 'F', label: 'No contract' },
    { value: 'OBT', label: 'Other' },
  ];

  const discriminationIssues = [
    {
      value: 'DR',
      label: 'Race or ethnicity',
    },
    {
      value: 'DS',
      label: 'Sex/gender',
    },
    {
      value: 'DP',
      label: 'Pregnancy',
    },
    {
      value: 'DM',
      label: 'Maternity',
    },
    {
      value: 'DD',
      label: 'Physical or Mental health condition',
    },
    {
      value: 'DA',
      label: 'Age',
    },
    {
      value: 'DRn',
      label: 'Religious belief',
    },
    {
      value: 'DPl',
      label: 'Philosophical belief',
    },
    {
      value: 'DSy',
      label: 'Sexual orientation',
    },
    {
      value: 'DG',
      label: 'Gender reassignment',
    },
    {
      value: 'DMe',
      label: 'Marital or civil partnership status',
    },
    {
      value: 'ORT',
      label: 'Other',
    },
  ];

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        const {
          data: {
            data: {
              attributes: { ParaPrompt },
            },
          },
        } = await getPrompts();
        setParaPrompt(ParaPrompt);
      } catch (error) {
        console.error('Error fetching issues:', error);
      }
    };
    fetchIssues();
  }, []);

  const isLastStep = step === steps.length - 1;

  const handleSubmissionError = (error, description, context = {}) => {
    console.error(error);
    setErrorMessage(
      error?.response?.data?.error?.details ?? 'There was an error when submitting your form',
    );
    Sentry.captureException(error, {
      tags: { page: 'ContactUsSteps' },
      extra: { description, context: { ...context } },
    });
  };

  const handleSubmit = async (values) => {
    if (step === 9) {
      try {
        await checkAvailability(values.email, values.email);
        setErrorMessage('');
      } catch (e) {
        handleSubmissionError(e, 'Step 9', values);
        return;
      }
    }
    const yearsEmployedLabel = getYearsEmployedLabelByValue(values['years-employed']);
    const stillEmployedLabel = getStillEmployedLabelByValue(values['still-employed']);
    if (step === 7) {
      try {
        const stillEmployedValue = values['still-employed'];
        const otherIssues =
          values.other_issues && values.other_issues.includes('none')
            ? []
            : values.other_issues
            ? [...values.other_issues, 'D']
            : [];
        const combinedIssues = [...values.employment_issues, ...otherIssues];
        const combinedTopics = [stillEmployedValue, values['years-employed'], ...combinedIssues];
        const { data } = await getStatements(stillEmployedValue, combinedIssues, combinedTopics);
        const statementList = data.map((item) => ({
          id: item.id,
          statement: item.title,
        }));
        let employmentIssuesSelected = '';
        if (values.employment_issues) {
          employmentIssuesSelected = employmentIssues.filter((employmentIssue) =>
            values.employment_issues.includes(employmentIssue.value),
          );
        }
        employmentIssuesSelected = employmentIssuesSelected.map((item) => item?.label).join(', ');
        let discriminationIssuesSelected = '';
        if (values.other_issues) {
          discriminationIssuesSelected = discriminationIssues.filter((discriminationIssue) =>
            values.other_issues.includes(discriminationIssue.value),
          );
        }
        discriminationIssuesSelected = discriminationIssuesSelected
          .map((item) => item?.label)
          .join(', ');
        // Prepare message for AI analysis
        const message = `Statements: ${JSON.stringify(statementList)}\n\nSummary: ${
          values.case_summary
        }
        \n\n### Are you still employed: ${stillEmployedLabel}
        \n\n### How long have you been in your job: ${yearsEmployedLabel}
        \n\n### Employment issues: ${employmentIssuesSelected ? employmentIssuesSelected : 'None'}
        \n\n### Discrimination issues: ${
          discriminationIssuesSelected ? discriminationIssuesSelected : 'None'
        }.`;
        const { data: { response } = {} } = await grappleAi(message, paraPrompt);

        const ids = JSON.parse(response || '[]');
        const filterQuery =
          ids.length > 0
            ? ids.map((id) => `&filters[id][$in]=${encodeURIComponent(id)}`).join('')
            : '&filters[id][$in]=';
        const { data: { data: paragraphsData } = {} } = await getParagraphs(filterQuery);
        const adviceNote = await getAdviceNotes(combinedTopics);
        const adviceNoteList = adviceNote?.data;
        const adviceNoteParagraph = adviceNoteList.map((item) => item.title).join('\n\n');
        const statementslist = paragraphsData.map((item) => ({
          value: item.id,
          text: item.attributes.Statement,
        }));

        if (statementslist.length > 0) {
          values['statementslist'] = statementslist;
          values['adviceNote'] = adviceNoteParagraph;
        }
        setStatementsList(statementslist);
        setErrorMessage('');
      } catch (error) {
        handleSubmissionError(error, 'Step 7', values);
        return;
      }
    }

    if (isLastStep) {
      setErrorMessage('');
      const ids = values.statementList;
      const filterQuery =
        ids.length > 0
          ? ids.map((id) => `&filters[id][$in]=${encodeURIComponent(id)}`).join('')
          : '&filters[id][$in]=';
      const { data: { data: paragraphsData } = {} } = await getParagraphs(filterQuery);
      const paragraphsText = paragraphsData
        .map((item) => item?.attributes?.Paragraph)
        .filter(Boolean)
        .join('\n\n');
      const statementsSelected = paragraphsData
        .map((item) => item?.attributes?.Statement)
        .filter(Boolean)
        .join(', ');
      let employmentIssuesSelected = '';
      if (values.employment_issues) {
        employmentIssuesSelected = employmentIssues.filter((employmentIssue) =>
          values.employment_issues.includes(employmentIssue.value),
        );
      }
      employmentIssuesSelected = employmentIssuesSelected.map((item) => item?.label).join(', ');
      let discriminationIssuesSelected = '';
      if (values.other_issues) {
        discriminationIssuesSelected = discriminationIssues.filter((discriminationIssue) =>
          values.other_issues.includes(discriminationIssue.value),
        );
      }
      discriminationIssuesSelected = discriminationIssuesSelected
        .map((item) => item?.label)
        .join(', ');
      const survey = {
        selected_letter: 'advice-note',
        employment_status: values['still-employed'],
        how_long: values['years-employed'],
        employment_issues: values.employment_issues,
        other_issues: values.other_issues,
        case_summary: values.case_summary,
        questions: values.questions,
        statements_list: values.statementList,
      };
      if (values.contactPreference.includes('terms')) {
        values.terms = true;
      } else {
        values.terms = false;
      }
      values.contact = true;
      values.formType = 'dihac_no_checkmark';
      if (!isUserLoggedIn) {
        values.tracking = getTrackingParams();
      }

      if (values.contactPreference.includes('create_account')) {
        values.create_account = true;
      } else {
        values.create_account = false;
      }

      const data = {
        paragraphsText,
        ...values,
        employmentIssuesSelected,
        discriminationIssuesSelected,
        statementsSelected,
        'still-employed': stillEmployedLabel,
        'years-employed': yearsEmployedLabel,
        employmentStatus: stillEmployedLabel,
        description: values.case_summary,
        survey,
      };
      try {
        const res = await axios
          .post(`${process.env.NEXT_PUBLIC_ROTA_URL}create-deal`, data)
          .then(legacyParseFirstJsonInResponse);
        const dealID = res?.data?.id;
        const deal_access_token = res?.data?.deal_access_token;
        saveGrappleReferral(res?.data);
        if (dealID) {
          const dihac_cdf3_url = `${
            window.location.origin
          }/do-i-have-a-case-cdf3?name=${encodeURIComponent(
            values.name,
          )}&email=${encodeURIComponent(
            values.email,
          )}&dealID=${dealID}&deal_access_token=${deal_access_token}`;
          const fdata = { ...data, dealID, deal_access_token, dihac_cdf3_url };
          const udate = { ...data, dealID, deal_access_token, dihac_cdf3_url };
          if (udate.other_issues && udate.other_issues.includes('none')) {
            delete udate.other_issues;
          }
          if (udate.survey.other_issues && udate.survey.other_issues.includes('none')) {
            delete udate.survey.other_issues;
          }
          await axios.post(`${process.env.NEXT_PUBLIC_ROTA_URL}update-deal`, udate);
          await generateAdviceNote(fdata);
        }
        window.location.href = thankYouPageFor('/thank-you-dihac');
      } catch (e) {
        handleSubmissionError(e, 'Last step', values);
        return;
      }
    }

    if (!isLastStep) {
      setStep((prevStep) => prevStep + 1);
    }
  };

  return (
    <>
      {step === 0 && (
        <>
          <h3 className="h3">Do I have a Case?</h3>
          <p className="p">Complete this info & get a free case analysis email from Monaco AI</p>
        </>
      )}
      <div className="flex flex-col items-center justify-center min-h-screen py-2 bg-gray-100">
        <div className="w-full max-w-md p-6 bg-white shadow-md rounded-md">
          <div className="flex justify-between items-center mb-6">
            {/* <div className="text-sm text-gray-500">
            Step {step + 1} of {steps.length}
          </div> */}
            <div className="step-desc">
              {step + 1}/{steps.length}
            </div>
            <div className="progress ">
              <div
                className="progress-child "
                style={{ width: `${((step + 1) / steps.length) * 100}%` }}
              ></div>
            </div>
          </div>
          <div className="cdf  cdf-new3">
            <Formik
              initialValues={{
                'still-employed': '',
                'years-employed': '',
                salary: '',
                'settlement-agreement': '',
                'what-is-your-objective': '',
                stages: '',
                employment_issues: '',
                other_issues: '',
                case_summary: '',
                questions: '',
                statementList: '',
                name: '',
                email: '',
                contactPreference: '',
                phone: '',
              }}
              validationSchema={validationSchema[step]}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched }) => {
                return (
                  <Form className="cdf__form">
                    {!isSubmitting && (
                      <>
                        {steps[step]}
                        {errorMessage && (
                          <span className="error footer-error">
                            <i className="cdf__field__error">{errorMessage}</i>
                          </span>
                        )}
                        <div className="flex justify-between mt-6 main-btns" key={step}>
                          {step > 0 && (
                            <button
                              type="button"
                              onClick={() => setStep(step - 1)}
                              className="text-blue-500 back-btn"
                            >
                              &lt; Back
                            </button>
                          )}

                          <button
                            type="submit"
                            disabled={isSubmitting}
                            id={`step${step}-next`}
                            data-step={step}
                            className=" sbmt-btn "
                          >
                            {isLastStep ? 'Submit' : 'Next'}
                          </button>
                        </div>
                      </>
                    )}
                    {isSubmitting && (
                      <div className="loader">
                        <img src="/images/loader.svg" alt="loader-image" />
                        <p>Please wait; this might take up to a minute.</p>
                      </div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};
